import { useLayoutEffect } from 'react';

const TOKEN_NAMES = [
  '--header-height',
  '--fs-h1',
  '--fs-h2',
  '--fs-h3',
  '--fs-p',
  '--fs-small',
  '--space-xs',
  '--space-sm',
  '--space-md',
  '--space-lg',
  '--space-xl',
  '--space-xxl',
  '--space-section-y',
  '--page-pad-desktop',
  '--page-pad-mobile',
  '--radius-sm',
  '--radius-btn',
  '--radius-card',
  '--line-thickness',
  '--icon-sm',
  '--icon-md',
  '--icon-lg',
  '--icon-xl',
  '--brand-mark-size',
  '--brand-ring-size',
  '--header-brand-mark-size',
  '--header-brand-ring-size',
  '--logo-mark-size',
  '--logo-ring-size',
  '--header-top-offset',
  '--header-inline-padding',
  '--header-action-gap',
  '--header-action-offset',
  '--header-logo-gap',
  '--header-logo-text-size',
  '--header-control-stroke',
  '--header-control-icon-size',
  '--header-control-hit-size',
  '--header-control-line-span',
  '--desktop-menu-control-hit-size',
  '--desktop-menu-control-line-span',
  '--line-thickness-thick',
  '--icon-translate-subtle',
];

function clampValue(min, preferred, max) {
  return Math.min(max, Math.max(min, preferred));
}

function snapToDevicePixels(value, devicePixelRatio) {
  return Math.round(value * devicePixelRatio) / devicePixelRatio;
}

function formatPx(value) {
  return `${Number(value.toFixed(4))}px`;
}

function computeSnappedTokens(root) {
  const viewportWidth = root.clientWidth || window.innerWidth;
  const rem = parseFloat(window.getComputedStyle(root).fontSize) || 16;
  const vw = viewportWidth / 100;
  const devicePixelRatio = window.devicePixelRatio || 1;
  const isMobileHeaderViewport = viewportWidth <= 700;

  const tokens = {
    '--header-height': clampValue(60, (4 * vw) + 40, 72),
    '--fs-h1': clampValue(1.75 * rem, vw + (1.4 * rem), 2.5 * rem),
    '--fs-h2': clampValue(1.5 * rem, (0.6 * vw) + (1.2 * rem), 1.875 * rem),
    '--fs-h3': clampValue(1.25 * rem, (0.35 * vw) + (1.1 * rem), 1.5 * rem),
    '--fs-p': clampValue(0.9375 * rem, (0.2 * vw) + (0.875 * rem), 1.0625 * rem),
    '--fs-small': clampValue(0.8125 * rem, (0.15 * vw) + (0.75 * rem), 0.9375 * rem),
    '--space-xs': clampValue(0.35 * rem, vw, 0.5 * rem),
    '--space-sm': clampValue(0.5 * rem, 1.5 * vw, 0.75 * rem),
    '--space-md': clampValue(0.75 * rem, 2 * vw, 1 * rem),
    '--space-lg': clampValue(1 * rem, 3 * vw, 1.35 * rem),
    '--space-xl': clampValue(1.35 * rem, 4 * vw, 1.8 * rem),
    '--space-xxl': clampValue(1.8 * rem, 5 * vw, 2.5 * rem),
    '--space-section-y': clampValue(2 * rem, 6 * vw, 4 * rem),
    '--page-pad-desktop': clampValue(1.2 * rem, 4 * vw, 2.5 * rem),
    '--page-pad-mobile': clampValue(0.5 * rem, 3 * vw, 1.25 * rem),
    '--radius-sm': clampValue(0.4 * rem, vw, 0.6 * rem),
    '--radius-btn': clampValue(0.8 * rem, 1.5 * vw, 1.2 * rem),
    '--radius-card': clampValue(0.9 * rem, 2 * vw, 1.25 * rem),
    '--line-thickness': 2,
    '--icon-sm': clampValue(14, (1.5 * vw) + 4, 18),
    '--icon-md': clampValue(18, (2 * vw) + 4, 22),
    '--icon-lg': clampValue(22, (3 * vw) + 4, 28),
    '--icon-xl': clampValue(28, (4 * vw) + 4, 36),
    '--header-top-offset': clampValue(0.5 * rem, 2 * vw, 1.25 * rem),
    '--header-inline-padding': clampValue(1 * rem, 3 * vw, 2 * rem),
    '--header-action-gap': clampValue(0.65 * rem, 1.8 * vw, 1 * rem),
    '--header-action-offset': clampValue(0.85 * rem, 2.2 * vw, 1.35 * rem),
    '--header-logo-gap': isMobileHeaderViewport ? clampValue(0.45 * rem, 1.8 * vw, 0.65 * rem) : clampValue(0.5 * rem, 1.5 * vw, 0.75 * rem),
    '--header-logo-text-size': isMobileHeaderViewport ? clampValue(0.95 * rem, 3 * vw, 1.05 * rem) : clampValue(1.25 * rem, (0.35 * vw) + (1.1 * rem), 1.5 * rem),
    '--header-control-stroke': 2,
    '--header-control-icon-size': 24,
    '--header-control-hit-size': isMobileHeaderViewport ? 36 : 40,
    '--header-control-line-span': isMobileHeaderViewport ? 20 : 24,
    '--desktop-menu-control-hit-size': 42,
    '--desktop-menu-control-line-span': 18,
    '--line-thickness-thick': 2.5,
    '--icon-translate-subtle': 0.5,
  };

  const snappedTokens = {};

  Object.entries(tokens).forEach(([name, value]) => {
    snappedTokens[name] = snapToDevicePixels(value, devicePixelRatio);
  });

  snappedTokens['--logo-mark-size'] = snapToDevicePixels(snappedTokens['--icon-xl'] * 1.3, devicePixelRatio);
  snappedTokens['--logo-ring-size'] = snapToDevicePixels(snappedTokens['--logo-mark-size'] + 6, devicePixelRatio);
  snappedTokens['--brand-mark-size'] = snappedTokens['--logo-mark-size'];
  snappedTokens['--brand-ring-size'] = snappedTokens['--logo-ring-size'];
  snappedTokens['--header-brand-mark-size'] = snapToDevicePixels(
    isMobileHeaderViewport ? clampValue(32, 8 * vw, 38) : snappedTokens['--brand-mark-size'],
    devicePixelRatio,
  );
  snappedTokens['--header-brand-ring-size'] = snapToDevicePixels(snappedTokens['--header-brand-mark-size'] + 6, devicePixelRatio);

  return snappedTokens;
}

export function applyDevicePixelSnappedTokens(root = document.documentElement) {
  if (!root || typeof window === 'undefined') {
    return;
  }

  const snappedTokens = computeSnappedTokens(root);

  Object.entries(snappedTokens).forEach(([name, value]) => {
    root.style.setProperty(name, formatPx(value));
  });
}

function clearDevicePixelSnappedTokens(root = document.documentElement) {
  if (!root) {
    return;
  }

  TOKEN_NAMES.forEach((name) => {
    root.style.removeProperty(name);
  });
}

export function useDevicePixelSnappedTokens() {
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    let frameId = 0;

    const updateTokens = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        applyDevicePixelSnappedTokens(document.documentElement);
      });
    };

    updateTokens();

    window.addEventListener('resize', updateTokens, { passive: true });
    window.visualViewport?.addEventListener('resize', updateTokens, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateTokens);
      window.visualViewport?.removeEventListener('resize', updateTokens);
      clearDevicePixelSnappedTokens(document.documentElement);
    };
  }, []);
}
